/**
 * Centralised navigation configuration for the Helix sidebar.
 *
 * Instead of four hardcoded arrays scattered inside Sidebar.tsx, every
 * navigation item is defined once in a registry (`NAV_ITEMS`) and then
 * referenced by key in the per-mode ordering arrays. The public
 * `buildNavigation()` function selects the right ordering at runtime.
 */

import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Settings,
  Calendar,
  ShoppingCart,
  CheckCircle,
  Users,
  FileText,
  History,
  ClipboardList,
  Newspaper,
  UserCog,
  Upload,
  UserPlus,
  AlertTriangle,
  Truck,
  BarChart3,
  Building2,
  Table,
  LineChart,
  FolderOpen,
  AlertOctagon,
  Database,
  PieChart,
  Box,
  Factory,
  Package,
  CalendarClock,
} from 'lucide-react';
import type { MenuVisibility } from '../contexts/MenuSettingsContext';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FeatureFlag =
  | 'delivery_appeals'
  | 'spend_leakage'
  | 'bulk_upload'
  | 'commodity_codes'
  | 'supplier_stock_levels'
  | 'switch_call_off_tab'
  | 'po_line_schedules'
  | 'schedule_collaboration'
  | 'allow_supplier_decline';

/** Shape consumed by the Sidebar rendering logic (unchanged from old code). */
export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  children?: NavItem[];
  visibilityKey?: keyof MenuVisibility;
  /**
   * Optional supplier-side override. When the user is viewing the supplier
   * sidebar, buildNavigation swaps `visibilityKey` → `supplierVisibilityKey`
   * so the item is gated by its own admin toggle instead of sharing state
   * with the customer-side key.
   */
  supplierVisibilityKey?: keyof MenuVisibility;
  tourId?: string;
  featureFlag?: FeatureFlag;
  /** Alternate label when portal context is flipped (Customer Portal mode) */
  flipName?: string;
}

// ---------------------------------------------------------------------------
// Item registry
// ---------------------------------------------------------------------------
// Each unique navigation entry is defined exactly once. Items that differ
// between standard and Barratt modes (different label, icon, etc.) are
// stored as separate keys (e.g. `suppliers` vs `barrattSupplier`).
// ---------------------------------------------------------------------------

const NAV_ITEMS = {
  // ---- Common ----
  dashboard: {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    visibilityKey: 'dashboard',
    supplierVisibilityKey: 'supplierDashboard',
    tourId: 'dashboard',
  },
  documents: {
    name: 'Documents',
    href: '/documents',
    icon: FileText,
    visibilityKey: 'documents',
    supplierVisibilityKey: 'supplierDocuments',
  },

  // ---- Standard customer ----
  supplierPerformance: {
    name: 'Supplier Performance',
    flipName: 'Customer Performance',
    href: '/suppliers/performance',
    icon: TrendingUp,
    visibilityKey: 'supplierPerformance',
  },
  reportsCustomer: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  calendarCustomer: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'calendar',
  },
  purchasing: {
    name: 'Purchasing',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchasing',
    tourId: 'purchasing',
  },
  partsMaster: {
    name: 'Parts Master',
    href: '/parts-master',
    icon: Package,
    visibilityKey: 'partsMaster' as keyof MenuVisibility,
  },
  partSchedule: {
    name: 'Part Schedule',
    href: '/part-schedule',
    icon: CalendarClock,
    visibilityKey: 'partSchedule' as keyof MenuVisibility,
  },
  qualityCustomer: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  suppliers: {
    name: 'Suppliers',
    flipName: 'Customers',
    href: '/suppliers',
    icon: Users,
    visibilityKey: 'suppliers',
    tourId: 'suppliers',
  },
  onboarding: {
    name: 'Supplier Onboarding',
    flipName: 'Customer Onboarding',
    href: '/onboarding',
    icon: UserPlus,
    visibilityKey: 'suppliers',
  },
  userInvitationsCustomer: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  userActivityHistory: {
    name: 'User Activity History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
  },
  questionnaire: {
    name: 'Questionnaires',
    href: '/questionnaires',
    icon: ClipboardList,
    visibilityKey: 'questionnaire',
  },
  newsReleaseCustomer: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'newsRelease',
  },
  deliveryAppeals: {
    name: 'Delivery Appeals',
    href: '/delivery-appeals',
    icon: AlertTriangle,
    featureFlag: 'delivery_appeals' as FeatureFlag,
  },
  spendLeakage: {
    name: 'Spend Leakage',
    href: '/spend-leakage',
    icon: TrendingDown,
    featureFlag: 'spend_leakage' as FeatureFlag,
  },
  dataImport: {
    name: 'Data Import',
    href: '/data-import',
    icon: Database,
    visibilityKey: 'dataImport' as keyof MenuVisibility,
  },
  importMonitor: {
    name: 'Import Monitor',
    href: '/import-monitor',
    icon: Upload,
  },
  settings: {
    name: 'My Account',
    href: '/settings',
    icon: Settings,
  },
  // ---- Standard supplier ----
  myOnboarding: {
    name: 'My Onboarding',
    href: '/onboarding/my-progress',
    icon: UserPlus,
    visibilityKey: 'supplierOnboarding',
    tourId: 'my-onboarding',
  },
  supplierQuestionnaires: {
    name: 'Questionnaires',
    // Route to the supplier's own onboarding/filled-dataset view which lists
    // every assigned questionnaire with its status (not_started / in_progress
    // / submitted / under_review / approved / declined / more_info_required)
    // and the expiry date. /intelligence/qualifications is the legacy
    // SupplierQualification page — a different data model that MAB doesn't
    // populate, so it rendered "No qualifications found" even when the
    // supplier had 3 approved FilledDatasets.
    href: '/onboarding/my-progress',
    icon: ClipboardList,
    visibilityKey: 'supplierQuestionnaires' as keyof MenuVisibility,
  },
  salesOrders: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchaseOrders',
    tourId: 'purchasing',
  },
  qualitySupplier: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  customers: {
    name: 'Customers',
    href: '/customers',
    icon: Users,
    visibilityKey: 'supplierCustomers' as keyof MenuVisibility,
    tourId: 'customers',
  },
  calendarSupplier: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'supplierCalendar' as keyof MenuVisibility,
  },
  userInvitationsSupplier: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  companyProfile: {
    name: 'Company Profile',
    href: '/suppliers',
    icon: Factory,
    visibilityKey: 'companyProfile',
  },
  newsReleaseSupplier: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'supplierNewsRelease' as keyof MenuVisibility,
  },
  reportsSupplier: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  stockLevels: {
    name: 'Stock Levels',
    href: '/stock-levels',
    icon: Box,
    featureFlag: 'supplier_stock_levels' as FeatureFlag,
  },

  // ---- Barratt customer ----
  barrattCategory: {
    name: 'Category',
    href: '/barratt/category',
    icon: FolderOpen,
    children: [
      {
        name: 'Spend Data',
        href: '/barratt/spend-data',
        icon: TrendingDown,
      },
    ],
  },
  barrattSupplier: {
    name: 'Supplier',
    href: '/suppliers',
    icon: Building2,
    visibilityKey: 'suppliers',
  },
  barrattDivisionPerformance: {
    name: 'Division Performance',
    href: '/barratt/division-performance',
    icon: BarChart3,
  },
  barrattDivisionScoreReport: {
    name: 'Division Score Report',
    href: '/barratt/division-score-report',
    icon: Table,
  },
  barrattCallOffPerformance: {
    name: 'Call Off Performance',
    href: '/barratt/call-off-performance',
    icon: LineChart,
  },
  barrattDataUploadCustomer: {
    name: 'Data Upload',
    href: '/barratt/data-upload',
    icon: Database,
  },
  barrattAnalyse: {
    name: 'Analyse',
    href: '/barratt/analyse',
    icon: TrendingDown,
  },
  barrattUserLoginHistory: {
    name: 'User Login History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
  },
  barrattSupplierUploadHistory: {
    name: 'Supplier Upload History',
    href: '/barratt/supplier-upload-history',
    icon: History,
  },

  // ---- Barratt supplier ----
  barrattCustomer: {
    name: 'Customer',
    href: '/customers',
    icon: Users,
    visibilityKey: 'customers',
  },
  barrattSalesOrders: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: Truck,
    visibilityKey: 'purchaseOrders',
  },
  barrattNCRs: {
    name: 'NCRs',
    href: '/quality',
    icon: AlertOctagon,
    visibilityKey: 'quality',
  },
  barrattDataUploadSupplier: {
    name: 'Data Upload',
    href: '/bulk-upload',
    icon: Upload,
  },

  // ---- Logistics ----
  logisticsCallOffs: {
    name: 'Dispatched Call-Offs',
    href: '/logistics/call-offs',
    icon: Truck,
  },
} satisfies Record<string, Omit<NavItem, 'children'> & { children?: Omit<NavItem, 'children'>[] }>;

type NavKey = keyof typeof NAV_ITEMS;

// ---------------------------------------------------------------------------
// Ordered sequences per mode
// ---------------------------------------------------------------------------
// Each array lists the registry keys in the exact display order that the
// old hardcoded arrays used. Changing the order here changes the sidebar.
// ---------------------------------------------------------------------------

const STANDARD_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'supplierPerformance',
  'reportsCustomer',
  'calendarCustomer',
  'purchasing',
  'partsMaster',
  'partSchedule',
  'qualityCustomer',
  'onboarding',
  'suppliers',
  'documents',
  'questionnaire',
  'newsReleaseCustomer',
  'userActivityHistory',
  'spendLeakage',
  'dataImport',
  'importMonitor',
];

const STANDARD_SUPPLIER_ORDER: NavKey[] = [
  'dashboard',
  'myOnboarding',
  'supplierQuestionnaires',
  'salesOrders',
  'calendarSupplier',
  'qualitySupplier',
  'customers',
  'documents',
  'newsReleaseSupplier',
  'stockLevels',
];

const BARRATT_CUSTOMER_ORDER: NavKey[] = [
  'barrattCategory',
  'barrattSupplier',
  'barrattDivisionPerformance',
  'barrattDivisionScoreReport',
  'barrattCallOffPerformance',
  'documents',
  'barrattDataUploadCustomer',
  'barrattAnalyse',
  'barrattUserLoginHistory',
  'barrattSupplierUploadHistory',
];

const BARRATT_SUPPLIER_ORDER: NavKey[] = [
  'barrattCustomer',
  'barrattSalesOrders',
  'barrattNCRs',
  'documents',
  'barrattDataUploadSupplier',
];

// CR-4143: Compliance module — stripped to onboarding/questionnaires/reports only
const COMPLIANCE_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'onboarding',
  'suppliers',
  'questionnaire',
  'reportsCustomer',
  'documents',
];

// Compliance supplier view — supplier sees only what they need to complete
const COMPLIANCE_SUPPLIER_ORDER: NavKey[] = [
  'myOnboarding',
  'supplierQuestionnaires',
  'documents',
];

// Logistics users — same sidebar as suppliers. The Call Off sub-tab inside
// Purchasing remains dispatched/delivered-only via CallOffViewSet.get_queryset()
// in the backend, so no separate nav entry is needed.
const LOGISTICS_ORDER: NavKey[] = [
  'dashboard',
  'myOnboarding',
  'supplierQuestionnaires',
  'salesOrders',
  'calendarSupplier',
  'qualitySupplier',
  'customers',
  'documents',
  'newsReleaseSupplier',
  'stockLevels',
];


// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export type PortalMode = 'customer' | 'supplier';

export interface BuildNavigationOptions {
  /** Which portal the user sees: customer or supplier */
  portalMode: PortalMode;
  /** Whether the application is running in Barratt mode */
  isBarratt: boolean;
  /** Whether the application is running in Compliance mode (CR-4143) */
  isCompliance?: boolean;
  /** Whether supplier is in Customer Portal owner mode (managing customers) */
  isCustomerPortalOwner?: boolean;
  /** Whether the user belongs to a logistics company (restricted view) */
  isLogistics?: boolean;
}

/**
 * Returns the ordered list of `NavItem` objects for the given combination of
 * portal mode and Barratt flag.
 *
 * This replaces the four hardcoded arrays that previously lived in
 * `Sidebar.tsx`: `customerNavigation`, `supplierNavigation`,
 * `barrattCustomerNavigation`, and `barrattSupplierNavigation`.
 */
export function buildNavigation({ portalMode, isBarratt, isCompliance, isCustomerPortalOwner, isLogistics }: BuildNavigationOptions): NavItem[] {
  let order: NavKey[];

  if (isLogistics) {
    order = LOGISTICS_ORDER;
  } else if (isCompliance) {
    order = portalMode === 'supplier' ? COMPLIANCE_SUPPLIER_ORDER : COMPLIANCE_CUSTOMER_ORDER;
  } else if (isBarratt) {
    order = portalMode === 'supplier' ? BARRATT_SUPPLIER_ORDER : BARRATT_CUSTOMER_ORDER;
  } else {
    order = portalMode === 'supplier' ? STANDARD_SUPPLIER_ORDER : STANDARD_CUSTOMER_ORDER;
  }

  return order.map(key => {
    const item = NAV_ITEMS[key];
    const navItem: NavItem = { ...item };
    if (isCustomerPortalOwner && navItem.flipName) {
      navItem.name = navItem.flipName;
    }
    if ('children' in item && item.children) {
      navItem.children = item.children.map(child => ({ ...child }));
    }
    return navItem;
  });
}
